import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api } from '@/modules/common/api/index.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import {
  GetApplicationOverTimeChartResponse,
  GetApplicationOverTimeChartV1SrChartsIssuerApplicationOverTimeGetParams,
  GetApplicationSummaryResponse,
  GetCapitalSummarySectionV1SrSectionsIssuerCapitalSummaryGetParams,
  GetIssuerCapitalSummarySectionResponse,
  GetPendingApplicationChartResponse,
} from 'api';

export const PENDING_APPLICATIONS_SUMMARY_QUERY_KEY = 'vestra/issuer-pending-applications-summary';
export const APPLICATIONS_OVER_TIME_SUMMARY_QUERY_KEY = 'vestra/issuer-applications-over-time';
export const PENDING_APPLICATIONS_KEY = 'vestra/issuer-pending-applications';
export const ISSUED_CAPITAL_SUMMARY_KEY = 'vestra/issuer-issued-capital-summary';

export const useIssuerPendingApplicationsSummaryQuery = (disabled = false) => {
  const registerId = useGetRegisterIdParam();

  return useQuery<GetApplicationSummaryResponse>({
    queryKey: [PENDING_APPLICATIONS_SUMMARY_QUERY_KEY, registerId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getPendingApplicationSummaryV1SrIssuerPendingApplicationSummaryGet();
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerApplicationsOverTimeQuery = (
  payload: GetApplicationOverTimeChartV1SrChartsIssuerApplicationOverTimeGetParams,
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();

  const { date_to, date_from, ...data } = payload;

  return useQuery<GetApplicationOverTimeChartResponse>({
    queryKey: [
      APPLICATIONS_OVER_TIME_SUMMARY_QUERY_KEY,
      registerId,
      JSON.stringify(data),
      date_to ? dayjs(date_to).format('YYYY-MM-DD') : null,
      date_from ? dayjs(date_from).format('YYYY-MM-DD') : null,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getApplicationOverTimeChartV1SrChartsIssuerApplicationOverTimeGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerPendingApplicationsQuery = (disabled = false) => {
  const registerId = useGetRegisterIdParam();

  return useQuery<GetPendingApplicationChartResponse>({
    queryKey: [PENDING_APPLICATIONS_KEY, registerId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getPendingApplicationChartV1SrChartsIssuerPendingApplicationGet();
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerGetIssuedCapitalSummaryQuery = (
  payload: GetCapitalSummarySectionV1SrSectionsIssuerCapitalSummaryGetParams = {},
  disabled = false,
) => {
  const registerId = useGetRegisterIdParam();

  const { date_to, date_from, ...data } = payload;

  return useQuery<GetIssuerCapitalSummarySectionResponse>({
    queryKey: [
      ISSUED_CAPITAL_SUMMARY_KEY,
      registerId,
      JSON.stringify(data),
      date_to ? dayjs(date_to).format('YYYY-MM-DD') : null,
      date_from ? dayjs(date_from).format('YYYY-MM-DD') : null,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getCapitalSummarySectionV1SrSectionsIssuerCapitalSummaryGet(
        payload,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};
